import React from 'react';
import { AiOutlineWarning, AiOutlineClose } from 'react-icons/ai';

/**
 * Modal que se muestra antes de confirmar create/edit/delete de una ACL.
 * Llama al endpoint `/acls/impact/{nodeId}` para mostrar cuántas series
 * descendientes serán reindexadas (cascade reindex async).
 *
 * Props:
 * - open: bool
 * - impact: AclImpactResponse | null
 * - operation: 'create' | 'edit' | 'delete'  → texto contextual
 * - onConfirm: () => void
 * - onCancel: () => void
 */
const AclImpactWarning = ({ open, impact, operation, onConfirm, onCancel }) => {
  if (!open || !impact) return null;
  const opLabel = { create: 'aplicar esta nueva', edit: 'modificar esta', delete: 'eliminar esta' }[operation] || 'cambiar esta';

  return (
    <div style={styles.overlay} data-testid="acl-impact-warning">
      <div style={styles.modal}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            <AiOutlineWarning style={{ color: '#D97706', marginRight: 8 }} />
            Confirmar cambio de ACL
          </h3>
          <button onClick={onCancel} style={styles.closeBtn} aria-label="Cerrar">
            <AiOutlineClose />
          </button>
        </div>

        <p style={styles.body}>
          Al {opLabel} ACL en <strong>{impact.nodeName}</strong> ({impact.nodeId}),
          {' '}los <code>allowed_groups</code> de <strong>{impact.affectedSeriesCount} serie{impact.affectedSeriesCount === 1 ? '' : 's'}</strong>
          {' '}descendiente{impact.affectedSeriesCount === 1 ? '' : 's'} serán recalculadas en cascada (async).
        </p>

        {impact.affectedSeriesCount > 0 && (
          <details style={styles.details}>
            <summary style={styles.summary}>
              Ver series afectadas {impact.truncated && '(primeras 50)'}
            </summary>
            <ul style={styles.list}>
              {impact.affectedSeries.map((s) => (
                <li key={s.id} style={styles.item}>
                  <code style={styles.serieId}>{s.id}</code> — {s.name}
                </li>
              ))}
            </ul>
          </details>
        )}

        <div style={styles.footer}>
          <button onClick={onCancel} style={styles.btnCancel}>Cancelar</button>
          <button onClick={onConfirm} style={styles.btnConfirm} data-testid="acl-impact-confirm">
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2500 },
  modal: { background: '#FFFFFF', borderRadius: '8px', padding: '24px', minWidth: '500px', maxWidth: '640px', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  title: { margin: 0, fontSize: '17px', color: '#111827', fontWeight: 'bold', display: 'flex', alignItems: 'center' },
  closeBtn: { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#6B7280' },
  body: { color: '#374151', fontSize: '14px', lineHeight: '1.5', marginBottom: '12px' },
  details: { border: '1px solid #E5E7EB', borderRadius: '6px', padding: '8px 12px', marginBottom: '16px' },
  summary: { cursor: 'pointer', fontSize: '13px', color: '#00468B', fontWeight: '600' },
  list: { listStyle: 'none', padding: 0, marginTop: '8px', maxHeight: '240px', overflowY: 'auto' },
  item: { padding: '4px 0', fontSize: '13px', color: '#374151' },
  serieId: { background: '#F3F4F6', padding: '2px 6px', borderRadius: '3px', fontSize: '12px' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' },
  btnCancel: { padding: '8px 16px', border: '1px solid #D1D5DB', background: '#FFFFFF', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  btnConfirm: { padding: '8px 16px', background: '#D97706', color: '#FFFFFF', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

export default AclImpactWarning;
